import BrowserRouter from '@fuse/core/BrowserRouter';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseTheme from '@fuse/core/FuseTheme';

import { SnackbarProvider } from 'notistack';
import { useSettingsSlice, selectMainTheme } from 'app/store/settingsSlice';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import mixpanel from 'mixpanel-browser';
import withAppProviders from './withAppProviders';
import AuthProvider from './auth/AuthContext';

import SuccessToast from './shared-components/toasts/SuccessToast';

import layout1 from './theme-layouts/layout/Layout';
import ErrorToast from './shared-components/toasts/ErrorToast';
import HotjarProvider from './HotjarProvider';
import { ModalsContainer } from './shared-components/modals/modalManager';
import { modalsManger as modalsManager } from './shared-components/modals';
import { SnackbarUtilsConfigurator } from './utils/snackbarUtils';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const mixpanelId = `${import.meta.env.VITE_APP_MIXPANEL_TOKEN}`;

  mixpanel.init(mixpanelId, { debug: true, track_pageview: true, persistence: 'localStorage' });

  const mainTheme = useSettingsSlice(selectMainTheme);

  return (
    <FuseTheme theme={mainTheme}>
      <Auth0Provider
        domain={`${import.meta.env.VITE_APP_API_DOMAIN ?? ''}`}
        clientId={`${import.meta.env.VITE_APP_API_CLIENTID ?? ''}`}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: import.meta.env.VITE_APP_API_AUDIENCE as string,
        }}
      >
        <BrowserRouter>
          <AuthProvider>
            <HotjarProvider />
            <HelmetProvider>
              <QueryClientProvider client={queryClient}>
                <SnackbarProvider
                  maxSnack={3}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  autoHideDuration={3000}
                  Components={{
                    successToast: SuccessToast,
                    errorToast: ErrorToast,
                  }}
                >
                  <ModalsContainer store={modalsManager.store} />
                  <SnackbarUtilsConfigurator />
                  <Helmet>
                    <title>PayBlox App | Start Here | Free Marketplace for Merchant Accounts</title>
                    <meta
                      name="description"
                      content="Start here for the PayBlox app. Credit card processing has never been simpler."
                    />
                  </Helmet>

                  <FuseLayout layouts={{ layout1 }} />
                </SnackbarProvider>
              </QueryClientProvider>
            </HelmetProvider>
          </AuthProvider>
        </BrowserRouter>
      </Auth0Provider>
    </FuseTheme>
  );
}

export default withAppProviders(App)();
